import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { SectionProps } from '../../utils/SectionProps';
import Modal from '../elements/Modal';
import { FiFilm, FiVideo, FiFileText, FiCheckSquare, FiCoffee, FiZap, FiPlay } from 'react-icons/fi';
import {
    ANDROID_PLAY_STORE_URL,
    IOS_APP_STORE_URL,
    redirectToMobileStore
} from '../../../../util/mobileStoreRedirect';

const pillStyle = (borderColor, textColor, bgGradient, isMobile = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: isMobile ? '4px' : '6px',
    verticalAlign: 'middle',
    padding: isMobile ? '3px 10px' : '5px 14px',
    margin: isMobile ? '2px 2px' : '3px 3px',
    borderRadius: '36px',
    border: `1px solid ${borderColor}`,
    background: bgGradient,
    color: textColor,
    fontSize: isMobile ? '13.5px' : '16px',
    fontWeight: 500,
    lineHeight: 1.3,
    whiteSpace: 'nowrap'
});

const heroAvatarImages = [
    { src: require('../../assets/images/testimonials/svetlana.webp'), alt: 'Svetlana' },
    { src: require('../../assets/images/testimonials/viktoria.webp'), alt: 'Viktoria' },
    { src: require('../../assets/images/testimonials/yago.webp'), alt: 'yago' },
    { src: require('../../assets/images/testimonials/mateus.webp'), alt: 'Mateus' },
    { src: require('../../assets/images/testimonials/lisa.webp'), alt: 'Lisa' },
    { src: require('../../assets/images/testimonials/john_lovelace.webp'), alt: 'John T. Lovelace' },
    { src: require('../../assets/images/testimonials/lino.webp'), alt: 'Lino' },
    { src: require('../../assets/images/testimonials/ashton_rogers.webp'), alt: 'Ashton Rogers' }
];

const heroImageDesktop = require('../../assets/images/hero-image-app.generated.webp');
const heroImageDesktopSmall = require('../../assets/images/hero-image-app.generated.small.webp');
const heroImagePhone = require('../../assets/images/hero-image-phone.generated.webp');
const heroImagePhoneSmall = require('../../assets/images/hero-image-phone.generated.small.webp');


const propTypes = {
    ...SectionProps.types
};

const defaultProps = {
    ...SectionProps.defaults
};

const Hero = ({
    className,
    topOuterDivider,
    bottomOuterDivider,
    topDivider,
    bottomDivider,
    hasBgColor,
    invertColor,
    ...props
}) => {
    const [videoModalActive, setVideomodalactive] = useState(false);
    // Match react-snap's desktop-first render on the first pass. Resolve the
    // actual viewport after hydration so mobile and desktop do not produce
    // different initial trees and force React to repaint the prerendered hero.
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        const updateViewport = () => setIsMobile(mediaQuery.matches);
        updateViewport();
        mediaQuery.addEventListener?.('change', updateViewport);
        return () => mediaQuery.removeEventListener?.('change', updateViewport);
    }, []);

    const openModal = (e) => {
        e.preventDefault();
        setVideomodalactive(true);
    };

    const closeModal = (e) => {
        e.preventDefault();
        setVideomodalactive(false);
    };

    const outerClasses = classNames(
        'hero section center-content',
        topOuterDivider && 'has-top-divider',
        bottomOuterDivider && 'has-bottom-divider',
        hasBgColor && 'has-bg-color',
        invertColor && 'invert-color',
        className
    );

    const innerClasses = classNames(
        'hero-inner section-inner',
        topDivider && 'has-top-divider',
        bottomDivider && 'has-bottom-divider'
    );

    return (
        <>
            <section
                {...props}
                className={outerClasses}
                aria-label="Hero"
                style={{
                    position: 'relative',
                    overflow: 'hidden',
                    backgroundColor: '#faf8f5'
                }}
            >
                <div className="hero-atmosphere" aria-hidden="true">
                    <span className="hero-atmosphere-orb hero-atmosphere-orb--blue" />
                    <span className="hero-atmosphere-orb hero-atmosphere-orb--peach" />
                    <span className="hero-atmosphere-orb hero-atmosphere-orb--lilac" />
                </div>

                <div className="hero-container" style={{ position: 'relative', zIndex: 1 }}>
                    <div className={innerClasses}>
                        <div className="hero-grid">
                            <div className="hero-content">
                                <div
                                    className="hero-eyebrow-capsule reveal-from-bottom"
                                    data-reveal-delay="300"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: isMobile ? '5px 12px' : '6px 16px',
                                        borderRadius: '9999px',
                                        background: 'rgba(255, 255, 255, 0.88)',
                                        border: '1px solid rgba(217, 119, 6, 0.24)',
                                        boxShadow: '0 2px 10px rgba(217, 119, 6, 0.06)',
                                        color: '#9a5518',
                                        fontSize: isMobile ? '12px' : '13.5px',
                                        fontWeight: 600,
                                        letterSpacing: '0.01em',
                                        marginBottom: isMobile ? '14px' : '18px'
                                    }}
                                >
                                    <span style={{ color: '#d97706', fontSize: isMobile ? '11px' : '12px' }}>✦</span>
                                    <span>Your life’s inbox</span>
                                </div>
                                <h1
                                    className="hero-title mt-0 mb-16"
                                    style={{
                                        lineHeight: isMobile ? 1.18 : 1.12,
                                        letterSpacing: '-0.03em'
                                    }}
                                >
                                    Everything you{' '}
                                    <span style={{ position: 'relative', display: 'inline-block', whiteSpace: 'nowrap' }}>
                                        save
                                        <svg
                                            viewBox="0 0 100 12"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            style={{
                                                position: 'absolute',
                                                left: 0,
                                                bottom: isMobile ? '-3px' : '-5px',
                                                width: '100%',
                                                height: isMobile ? '6px' : '9px',
                                                pointerEvents: 'none'
                                            }}
                                            preserveAspectRatio="none"
                                        >
                                            <path
                                                d="M2 8C30 3 70 3 98 8"
                                                stroke="#e09f67"
                                                strokeWidth={isMobile ? "3" : "4.5"}
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </span>
                                    ,<br />
                                    working for you.
                                </h1>
                                <p
                                    className="hero-subhead m-0 mb-32 reveal-from-bottom"
                                    data-reveal-delay="400"
                                    style={{
                                        maxWidth: isMobile ? '580px' : '590px',
                                        fontSize: isMobile ? '15px' : '19px',
                                        lineHeight: isMobile ? '32px' : '44px',
                                        color: '#4A5465',
                                        letterSpacing: '-0.01em',
                                        textAlign: 'center',
                                        marginLeft: 'auto',
                                        marginRight: 'auto',
                                        paddingLeft: isMobile ? '8px' : 0,
                                        paddingRight: isMobile ? '8px' : 0
                                    }}
                                >
                                    Send Luckynote any{' '}
                                    <span style={pillStyle('#E48ABF', '#D965A5', 'linear-gradient(180deg, rgba(228, 138, 191, 0.08) 0%, rgba(228, 138, 191, 0.02) 100%)', isMobile)}>
                                        <FiFilm size={isMobile ? 11 : 13} style={{ flexShrink: 0, opacity: 0.85 }} />
                                        <span>reel</span>
                                    </span>
                                    ,{' '}
                                    <span style={pillStyle('#99D2FB', '#3B82F6', 'linear-gradient(180deg, rgba(153, 210, 251, 0.12) 0%, rgba(153, 210, 251, 0.02) 100%)', isMobile)}>
                                        <FiVideo size={isMobile ? 11 : 13} style={{ flexShrink: 0, opacity: 0.85 }} />
                                        <span>video</span>
                                    </span>
                                    , or{' '}
                                    <span style={pillStyle('#E98F53', '#D97706', 'linear-gradient(180deg, rgba(233, 143, 83, 0.10) 0%, rgba(233, 143, 83, 0.02) 100%)', isMobile)}>
                                        <FiFileText size={isMobile ? 11 : 13} style={{ flexShrink: 0, opacity: 0.85 }} />
                                        <span>article</span>
                                    </span>
                                    .{' '}
                                    It reads it, pulls out{' '}
                                    <span style={pillStyle('#86EFAC', '#16A34A', 'linear-gradient(180deg, rgba(134, 239, 172, 0.12) 0%, rgba(134, 239, 172, 0.02) 100%)', isMobile)}>
                                        <FiCheckSquare size={isMobile ? 11 : 13} style={{ flexShrink: 0, opacity: 0.85 }} />
                                        <span>the recipe</span>
                                    </span>
                                    ,{' '}
                                    <span style={pillStyle('#FFCF27', '#CA8A04', 'linear-gradient(180deg, rgba(255, 207, 39, 0.12) 0%, rgba(255, 207, 39, 0.02) 100%)', isMobile)}>
                                        <FiCoffee size={isMobile ? 11 : 13} style={{ flexShrink: 0, opacity: 0.85 }} />
                                        <span>the café list</span>
                                    </span>
                                    , or{' '}
                                    <span style={pillStyle('#D8B4FE', '#9333EA', 'linear-gradient(180deg, rgba(216, 180, 254, 0.12) 0%, rgba(216, 180, 254, 0.02) 100%)', isMobile)}>
                                        <FiZap size={isMobile ? 11 : 13} style={{ flexShrink: 0, opacity: 0.85 }} />
                                        <span>the key points</span>
                                    </span>
                                    {' '}— and brings it back when it’s time to act.
                                </p>

                                <div
                                    className="hero-ctas reveal-from-bottom"
                                    data-reveal-delay="500"
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '12px'
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: isMobile ? 'column' : 'row',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '10px',
                                            width: isMobile ? '100%' : 'auto'
                                        }}
                                    >
                                        <a
                                            href="/register"
                                            onClick={redirectToMobileStore}
                                            className="button button-primary"
                                            style={{
                                                minWidth: isMobile ? '180px' : '152px',
                                                height: '46px',
                                                paddingLeft: '24px',
                                                paddingRight: '24px',
                                                fontSize: '16px'
                                            }}
                                        >
                                            Start free
                                        </a>
                                        <button
                                            type="button"
                                            onClick={openModal}
                                            aria-haspopup="dialog"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px',
                                                minWidth: isMobile ? '180px' : '152px',
                                                height: '46px',
                                                padding: '0 24px',
                                                border: 0,
                                                borderRadius: '9px',
                                                background: '#f1eeea',
                                                color: '#14110f',
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s ease, color 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = '#e6e1db';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#f1eeea';
                                            }}
                                        >
                                            <FiPlay size={14} style={{ fill: '#3b82f6', color: '#3b82f6' }} />
                                            <span>Watch demo</span>
                                        </button>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#667085' }}>
                                        Available for{' '}
                                        <a
                                            href={IOS_APP_STORE_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#2b88d9', fontWeight: 600 }}
                                        >
                                            iOS
                                        </a>
                                        {' '}and{' '}
                                        <a
                                            href={ANDROID_PLAY_STORE_URL}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: '#2b88d9', fontWeight: 600 }}
                                        >
                                            Android
                                        </a>
                                    </p>
                                </div>{/* end hero-ctas */}
                            </div>

                            {/* Product visual */}
                            {/* No entrance-animation classes here: this figure holds the LCP
                                image and must be fully visible in the prerendered HTML with
                                no opacity/transform gating that depends on JS running. */}
                            <div className="hero-figure">
                                <div className="hero-figure-inner">
                                    <img
                                        className="hero-img-desktop"
                                        src={heroImageDesktop}
                                        srcSet={`${heroImageDesktopSmall} 700w, ${heroImageDesktop} 1400w`}
                                        sizes="(max-width: 767px) 315px, 913px"
                                        alt="Luckynote app on desktop"
                                        width={1400}
                                        height={886}
                                        loading="eager"
                                        fetchpriority="high"
                                        decoding="async"
                                    />
                                    <img
                                        className="hero-img-phone"
                                        src={heroImagePhone}
                                        srcSet={`${heroImagePhoneSmall} 300w, ${heroImagePhone} 600w`}
                                        sizes="(max-width: 767px) 95px, 275px"
                                        alt="Luckynote app on mobile"
                                        width={600}
                                        height={1222}
                                        loading="eager"
                                        decoding="async"
                                    />
                                </div>
                            </div>

                            {/* Social proof below the picture - Unified Glassmorphism Capsule */}
                            <div
                                className="hero-social-proof"
                                style={{
                                    order: 3,
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    marginTop: isMobile ? '20px' : '44px',
                                    marginBottom: isMobile ? '28px' : '48px',
                                    paddingLeft: '16px',
                                    paddingRight: '16px'
                                }}
                            >
                                <div
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: isMobile ? '8px' : '14px',
                                        padding: isMobile ? '5px 12px 5px 8px' : '7px 18px 7px 10px',
                                        background: 'rgba(255, 255, 255, 0.92)',
                                        backdropFilter: 'blur(12px)',
                                        WebkitBackdropFilter: 'blur(12px)',
                                        borderRadius: '9999px',
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.06), 0 2px 6px rgba(0, 0, 0, 0.03)',
                                        maxWidth: '100%'
                                    }}
                                >
                                    {/* Avatars group on the left */}
                                    <div style={{ display: 'flex', alignItems: 'center' }} aria-hidden="true">
                                        {heroAvatarImages.slice(0, isMobile ? 4 : 5).map((img, i) => (
                                            <div
                                                key={img.alt}
                                                style={{
                                                    width: isMobile ? 24 : 30,
                                                    height: isMobile ? 24 : 30,
                                                    borderRadius: '50%',
                                                    border: '2px solid #ffffff',
                                                    overflow: 'hidden',
                                                    marginLeft: i === 0 ? 0 : (isMobile ? -6 : -7),
                                                    flexShrink: 0,
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
                                                }}
                                            >
                                                <img
                                                    src={img.src}
                                                    alt={img.alt}
                                                    width={isMobile ? 24 : 30}
                                                    height={isMobile ? 24 : 30}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Subtle vertical divider */}
                                    <div style={{ width: 1, height: 14, background: 'rgba(0, 0, 0, 0.12)', flexShrink: 0 }} aria-hidden="true" />

                                    {/* Stars and rating on the right */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', whiteSpace: 'nowrap' }}>
                                        <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#f59e0b', letterSpacing: '0.5px', lineHeight: 1 }} aria-hidden="true">★★★★★</span>
                                        <span style={{ fontSize: isMobile ? '12px' : '13.5px', fontWeight: 600, color: '#18181b', lineHeight: 1 }}>4.6</span>
                                        <span style={{ fontSize: '11px', color: '#a1a1aa' }}>•</span>
                                        <span style={{ fontSize: isMobile ? '11.5px' : '13.5px', color: '#52525b', fontWeight: 500, lineHeight: 1 }}>{isMobile ? '6,000+ users' : 'Loved by 6,000+ users'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Modal
                id="video-modal"
                show={videoModalActive}
                handleClose={closeModal}
                video="https://www.youtube.com/embed/lDDtsYRZ0-Q"
                videoTag="iframe"
            />
        </>
    );
};

Hero.propTypes = propTypes;
Hero.defaultProps = defaultProps;

export default Hero;
